import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDate, formatDateTime } from "@/lib/format";

type DeliveryNote = components["schemas"]["DeliveryNoteResponse"];
type DeliveryNoteStatus = components["schemas"]["DeliveryNoteStatus"];

interface DeliveryNotesTableProps {
  organisationId?: string;
}

function statusVariant(
  status: DeliveryNoteStatus | undefined,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "FullyMatched":
      return "default";
    case "PartiallyMatched":
      return "secondary";
    case "Cancelled":
      return "destructive";
    default:
      return "outline";
  }
}

export function DeliveryNotesTable({ organisationId }: DeliveryNotesTableProps) {
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [notes, setNotes] = useState<DeliveryNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchNotes() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/delivery-notes", {
        params: {
          query: {
            OrganisationId: organisationId,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load delivery notes.");
      } else {
        setNotes(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchNotes();
    return () => {
      cancelled = true;
    };
  }, [organisationId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">{loading && <Spinner className="size-5" />}</div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>DN #</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Purchase order</TableHead>
              <TableHead>Received</TableHead>
              <TableHead>Lines</TableHead>
              <TableHead>Last modified</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {notes.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground h-24 text-center">
                  No delivery notes found.
                </TableCell>
              </TableRow>
            ) : (
              notes.map((dn) => (
                <TableRow key={dn.id}>
                  <TableCell className="font-mono font-medium">
                    {dn.id ? (
                      <Link to={`/delivery-notes/${dn.id}`} className="hover:underline">
                        {dn.dnNumber || dn.id.slice(0, 8) + "..."}
                      </Link>
                    ) : (
                      (dn.dnNumber ?? <span className="text-muted-foreground">—</span>)
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(dn.status)}>{dn.status}</Badge>
                  </TableCell>
                  <TableCell>
                    {dn.supplierId ? (
                      <Link to={`/suppliers/${dn.supplierId}`} className="hover:underline">
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {dn.supplierId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {dn.purchaseOrderId ? (
                      <Link to={`/purchase-orders/${dn.purchaseOrderId}`} className="hover:underline">
                        <Badge variant="outline" className="font-mono text-xs hover:bg-primary/20">
                          {dn.purchaseOrderId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDate(dn.receivedAt) || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{dn.lines?.length ?? 0}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDateTime(dn.updatedAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
